import {styled} from 'styled-components';
import React, {useRef, useState, useEffect} from 'react';
import {StyledProps} from '../../modules/common';
import {CenteredColumnContainer} from '../centered-column-container';
import {shadowBottomLong} from '../shadows.style';

interface StyleHiddenNavProps extends StyledProps {
    backgroundColor?: string;
    children: React.ReactNode;
    visible: boolean;
    firstIteration: boolean;
}

interface OuterWrapperProps {
    $visible: boolean;
    $firstIteration: boolean;
    height: number;
}

const OuterWrapper = styled.div<OuterWrapperProps>`
    ${(props: OuterWrapperProps): string =>
        props.$visible &&
        `animation: show .5s;
     animation-fill-mode: forwards;`}

    ${(props: OuterWrapperProps): string =>
        !props.$visible &&
        !props.$firstIteration &&
        `animation: hide 0.5s;
     animation-fill-mode: forwards;`}    
    
    ${(props: OuterWrapperProps): string =>
        !props.$visible && props.$firstIteration && `visibility: hidden;`}    
                
    @keyframes show {
        from {
            z-index: 10;
            visibility: visible;
            transform: translateY(
                -${(props): string => `${props.height}${props.theme.unitsOfMeasure.px}`}
            );
        }
        to {
            transform: translateY(0px);
            z-index: 1030;
        }
    }

    @keyframes hide {
        from {
            z-index: 10;
            transform: translateY(0px);
        }
        to {
            transform: translateY(
                -${(props): string => `${props.height}${props.theme.unitsOfMeasure.px}`}
            );
            visibility: hidden;
            z-index: 10;
        }
    }
`;

/**
 * Hidden navbar component.
 *
 * This component renders its children bellow the static navar when the prop visible is true.
 */
const UnstyleHiddenNav = (props: StyleHiddenNavProps): JSX.Element => {
    const {className, visible, firstIteration, children} = props;
    const wrapper = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number>(0);

    useEffect(() => {
        setHeight(wrapper.current.clientHeight);
        window.addEventListener('resize', updateSizes);
        return () => {
            window.removeEventListener('resize', updateSizes);
        };
    }, []);

    const updateSizes = (): void => {
        setHeight(wrapper.current.clientHeight);
    };

    return (
        <OuterWrapper
            className={className}
            $visible={visible}
            $firstIteration={firstIteration}
            height={height}
            ref={wrapper}
        >
            <CenteredColumnContainer>{children}</CenteredColumnContainer>
        </OuterWrapper>
    );
};

export const HiddenNav = styled(UnstyleHiddenNav)`
    background-color: ${(props): string =>
        props.backgroundColor
            ? props.backgroundColor
            : props.theme.navBar.backgroundColor};
    position: fixed;
    right: 0;
    left: 0;
    top: 0;
    z-index: 1030;
    height: fit-content;

    div {
        text-align: center;
    }

    ${shadowBottomLong}
`;
